/**
 * Features_BentoA - Bento Grid Feature Page
 * 
 * Modern Apple-style bento box grid layout.
 * Each feature in its own cell with icons and descriptions.
 * Clean, structured, easy to scan.
 */

import { Link } from 'react-router-dom';
import './Features_BentoA.css';

const HERO_FEATURES = [
  { icon: 'cloud_off', label: '100% Offline', desc: 'Works without internet' },
  { icon: 'devices', label: 'All Devices', desc: 'Phone, tablet, desktop' },
  { icon: 'palette', label: '9 Themes', desc: 'Beautiful dark modes' },
  { icon: 'lock', label: 'Privacy First', desc: 'No bank connections' },
];

const MAIN_FEATURES = [
  {
    id: 'offline',
    size: 'large',
    title: 'Offline-First Architecture',
    subtitle: 'Your app works everywhere. Always.',
    bullets: [
      'All data stored locally - instant reads and writes',
      'Works on airplanes, subways, remote areas',
      'Syncs automatically when back online',
      'No loading spinners waiting for servers',
    ],
    accent: '#7aa2f7',
    icon: 'wifi_off',
  },
  {
    id: 'sync',
    size: 'medium',
    title: 'Multi-Device Sync',
    subtitle: 'One account. All devices.',
    bullets: [
      'Real-time sync across devices',
      '~60ms for 1,756 records',
      'Works with unstable connections',
    ],
    accent: '#bb9af7',
    icon: 'sync',
  },
  {
    id: 'analytics',
    size: 'medium',
    title: 'Smart Analytics',
    subtitle: 'Know your financial health.',
    bullets: [
      'Cash Flow Score (0-100)',
      'Spending by category charts',
      'Month-over-month comparison',
    ],
    accent: '#9ece6a',
    icon: 'insights',
  },
  {
    id: 'transactions',
    size: 'small',
    title: 'Quick Transactions',
    subtitle: 'Add expenses in seconds',
    accent: '#7dcfff',
    icon: 'add_circle',
  },
  {
    id: 'categories',
    size: 'small',
    title: '64+ Categories',
    subtitle: 'Pre-built organization',
    accent: '#e0af68',
    icon: 'category',
  },
  {
    id: 'import',
    size: 'small',
    title: 'Universal Import',
    subtitle: 'OFX, QFX, QIF, CSV, JSON',
    accent: '#f7768e',
    icon: 'upload_file',
  },
  {
    id: 'privacy',
    size: 'wide',
    title: 'Privacy & Security',
    subtitle: 'Your data stays yours',
    bullets: [
      'No bank connections required - manual entry only',
      'Privacy Mode hides balances in public',
      'GDPR & CCPA compliant',
      'Complete account deletion available',
    ],
    accent: '#c0caf5',
    icon: 'shield',
  },
  {
    id: 'themes',
    size: 'medium',
    title: '9 Dark Themes',
    subtitle: 'Express your style',
    themes: ['Tokyo Night', 'Midnight Blue', 'Emerald Dark', 'Rose Dark', 'Amber Dark', 'Ninja Stealth', 'Ocean Depths', 'Sunset Glow', 'Lavender Mist'],
    accent: '#bb9af7',
    icon: 'palette',
  },
  {
    id: 'recurring',
    size: 'medium',
    title: 'Smart Recurring',
    subtitle: 'Auto-detect your bills',
    bullets: [
      'Finds recurring patterns automatically',
      'Overdue tracking & reminders',
      'Skip, dismiss, or pay with one tap',
    ],
    accent: '#7aa2f7',
    icon: 'event_repeat',
  },
  {
    id: 'pwa',
    size: 'wide',
    title: 'Install Like a Native App',
    subtitle: 'Progressive Web App - works everywhere',
    bullets: [
      'Android: Chrome menu > "Add to Home Screen"',
      'iOS: Safari share > "Add to Home Screen"',
      'Desktop: Click install icon in address bar',
      'Auto-updates, fast loading, works offline',
    ],
    accent: '#7dcfff',
    icon: 'install_mobile',
  },
];

const PRICING = [
  { tier: 'Free', price: '$0', features: ['Full app', 'Single device', 'All themes', 'Import/Export'] },
  { tier: 'Pro', price: '$4.99/mo', features: ['Everything free', 'Multi-device sync', 'Cloud backup', '3-day trial'] },
  { tier: 'Lifetime', price: '$49', features: ['Pro forever', 'One-time payment', 'Limited to 100'] },
];

export default function Features_BentoA() {
  return (
    <div className="feat-bento">
      {/* Header */}
      <header className="feat-bento-header">
        <Link to="/" className="feat-bento-logo">NinjaCash</Link>
        <nav className="feat-bento-nav">
          <Link to="/" className="feat-bento-nav-link">Home</Link>
          <Link to="/pricing" className="feat-bento-nav-link">Pricing</Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="feat-bento-hero">
        <h1 className="feat-bento-title">
          <span className="feat-bento-title-line">Personal Finance</span>
          <span className="feat-bento-title-accent">That Just Works</span>
        </h1>
        <p className="feat-bento-tagline">
          Offline-first. Privacy-focused. Beautifully designed.
        </p>
        <div className="feat-bento-hero-stats">
          {HERO_FEATURES.map((f) => (
            <div key={f.label} className="feat-bento-stat">
              <span className="material-symbols-outlined feat-bento-stat-icon">{f.icon}</span>
              <span className="feat-bento-stat-label">{f.label}</span>
              <span className="feat-bento-stat-desc">{f.desc}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Bento Grid */}
      <section className="feat-bento-grid">
        {MAIN_FEATURES.map((feat) => (
          <article
            key={feat.id}
            className={`feat-bento-card feat-bento-card--${feat.size}`}
            style={{ '--card-accent': feat.accent } as React.CSSProperties}
          >
            <div className="feat-bento-card-header">
              <span className="material-symbols-outlined feat-bento-card-icon">{feat.icon}</span>
              <div className="feat-bento-card-titles">
                <h3 className="feat-bento-card-title">{feat.title}</h3>
                <p className="feat-bento-card-subtitle">{feat.subtitle}</p>
              </div>
            </div>
            {feat.bullets && (
              <ul className="feat-bento-card-bullets">
                {feat.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
            {feat.themes && (
              <div className="feat-bento-themes">
                {feat.themes.map((t) => (
                  <span key={t} className="feat-bento-theme-tag">{t}</span>
                ))}
              </div>
            )}
          </article>
        ))}
      </section>

      {/* Pricing Preview */}
      <section className="feat-bento-pricing">
        <h2 className="feat-bento-section-title">Simple Pricing</h2>
        <div className="feat-bento-pricing-grid">
          {PRICING.map((p) => (
            <div key={p.tier} className={`feat-bento-price-card ${p.tier === 'Pro' ? 'feat-bento-price-card--featured' : ''}`}>
              <h3 className="feat-bento-price-tier">{p.tier}</h3>
              <p className="feat-bento-price-amount">{p.price}</p>
              <ul className="feat-bento-price-features">
                {p.features.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="feat-bento-cta">
        <h2 className="feat-bento-cta-title">Ready to take control?</h2>
        <p className="feat-bento-cta-desc">Start tracking your finances today. Free forever on one device.</p>
        <Link to="/" className="feat-bento-cta-btn">Get Started Free</Link>
      </section>

      {/* Footer */}
      <footer className="feat-bento-footer">
        <div className="feat-bento-footer-links">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <a href="https://github.com/TakashiKyoto/ninjacash" target="_blank" rel="noopener noreferrer">GitHub</a>
        </div>
        <p className="feat-bento-footer-copy">&copy; 2026 NinjaCash. Your money, your device, your privacy.</p>
      </footer>
    </div>
  );
}
